/**
 * Hook to detect timezone changes when the app loads.
 * 
 * Compares the device's current timezone with the stored notification_timezone
 * in the user's profile. If they differ (e.g., user traveled to a new timezone),
 * surfaces the mismatch so a UI prompt can ask the user to update.
 * 
 * Only shows the prompt once per session to avoid being annoying.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import type { UserProfile } from '@/types/database'
import { supabase } from '@/lib/supabase'

/** Human-readable labels for common IANA timezones */
const TIMEZONE_LABELS: Record<string, string> = {
  'America/New_York': 'New York (Eastern)',
  'America/Chicago': 'Chicago (Central)',
  'America/Denver': 'Denver (Mountain)',
  'America/Los_Angeles': 'Los Angeles (Pacific)',
  'America/Anchorage': 'Anchorage (Alaska)',
  'Pacific/Honolulu': 'Honolulu (Hawaii)',
  'America/Phoenix': 'Phoenix (Arizona)',
  'America/Toronto': 'Toronto',
  'America/Vancouver': 'Vancouver',
  'America/Sao_Paulo': 'São Paulo',
  'America/Mexico_City': 'Mexico City',
  'America/Argentina/Buenos_Aires': 'Buenos Aires',
  'Europe/London': 'London',
  'Europe/Paris': 'Paris',
  'Europe/Berlin': 'Berlin',
  'Europe/Madrid': 'Madrid',
  'Europe/Rome': 'Rome',
  'Europe/Amsterdam': 'Amsterdam',
  'Europe/Zurich': 'Zurich',
  'Europe/Moscow': 'Moscow',
  'Europe/Istanbul': 'Istanbul',
  'Asia/Jerusalem': 'Jerusalem',
  'Asia/Dubai': 'Dubai',
  'Asia/Kolkata': 'Mumbai / Delhi',
  'Asia/Bangkok': 'Bangkok',
  'Asia/Singapore': 'Singapore',
  'Asia/Shanghai': 'Shanghai',
  'Asia/Hong_Kong': 'Hong Kong',
  'Asia/Tokyo': 'Tokyo',
  'Asia/Seoul': 'Seoul',
  'Australia/Sydney': 'Sydney',
  'Australia/Melbourne': 'Melbourne',
  'Pacific/Auckland': 'Auckland',
  'UTC': 'UTC',
}

/** Get a human-readable label for an IANA timezone string */
export function getTimezoneLabel(tz: string): string {
  if (TIMEZONE_LABELS[tz]) return TIMEZONE_LABELS[tz]
  // Fallback: convert "America/New_York" → "New York"
  const parts = tz.split('/')
  const city = parts[parts.length - 1].replace(/_/g, ' ')
  return city
}

/** Get the current UTC offset string like "UTC+3" or "UTC-5" */
function getUtcOffset(tz: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(now)
    const offsetPart = parts.find(p => p.type === 'timeZoneName')
    return offsetPart?.value || ''
  } catch {
    return ''
  }
}

const SESSION_KEY = 'miac:tz-prompt-dismissed'
const PERMANENT_KEY = 'miac:tz-prompt-never-show'

export interface TimezoneChange {
  /** The timezone stored in the user's profile (what notifications are scheduled with) */
  storedTimezone: string
  storedTimezoneLabel: string
  storedOffset: string
  /** The device's current timezone */
  deviceTimezone: string
  deviceTimezoneLabel: string
  deviceOffset: string
}

export function useTimezoneDetection(profile: UserProfile | null, userId: string | undefined) {
  const [timezoneChange, setTimezoneChange] = useState<TimezoneChange | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const checkedRef = useRef(false)

  // Detect timezone mismatch when profile loads
  useEffect(() => {
    if (!profile || !userId) return
    if (checkedRef.current) return
    checkedRef.current = true

    const storedTz = profile.notification_timezone
    const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone

    console.log('[TZ-Detection] Checking:', {
      storedTz,
      deviceTz,
      match: storedTz === deviceTz,
      permanentDismiss: localStorage.getItem(PERMANENT_KEY),
      sessionDismiss: sessionStorage.getItem(SESSION_KEY),
      profileId: profile.id,
      displayName: profile.display_name,
    })

    // Don't prompt if user permanently opted out
    if (localStorage.getItem(PERMANENT_KEY)) return

    // Don't prompt again if user already dismissed this session
    if (sessionStorage.getItem(SESSION_KEY)) return

    // No stored timezone yet — silently set it (first-time setup)
    if (!storedTz) {
      console.log('[TZ-Detection] No stored TZ, silently setting to', deviceTz)
      supabase
        .from('user_profiles')
        .update({ notification_timezone: deviceTz })
        .eq('id', userId)
        .then(() => {
          // Dispatch event so layout can refetch
          window.dispatchEvent(new Event('miac:profile-refresh'))
        })
      return
    }

    // Same timezone — nothing to do
    if (storedTz === deviceTz) {
      console.log('[TZ-Detection] Timezones match, no prompt needed')
      return
    }

    // Timezone mismatch detected!
    console.log('[TZ-Detection] MISMATCH! Showing drawer')
    setTimezoneChange({
      storedTimezone: storedTz,
      storedTimezoneLabel: getTimezoneLabel(storedTz),
      storedOffset: getUtcOffset(storedTz),
      deviceTimezone: deviceTz,
      deviceTimezoneLabel: getTimezoneLabel(deviceTz),
      deviceOffset: getUtcOffset(deviceTz),
    })
  }, [profile, userId])

  /** User approved the timezone update */
  const approveChange = useCallback(async () => {
    if (!timezoneChange || !userId) return

    setIsUpdating(true)
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ notification_timezone: timezoneChange.deviceTimezone })
        .eq('id', userId)

      if (!error) {
        // Mark as handled for this session
        sessionStorage.setItem(SESSION_KEY, 'true')
        setTimezoneChange(null)
        // Refresh profile data across the app
        window.dispatchEvent(new Event('miac:profile-refresh'))
      }
    } finally {
      setIsUpdating(false)
    }
  }, [timezoneChange, userId])

  /** User wants to keep the old timezone for now (dismiss for this session) */
  const dismissChange = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, 'true')
    setTimezoneChange(null)
  }, [])

  /** User never wants to see this prompt again */
  const neverShowAgain = useCallback(() => {
    localStorage.setItem(PERMANENT_KEY, 'true')
    sessionStorage.setItem(SESSION_KEY, 'true')
    setTimezoneChange(null)
  }, [])

  return {
    timezoneChange,
    isUpdating,
    approveChange,
    dismissChange,
    neverShowAgain,
  }
}
